import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateAccountContext } from "../../provider/createAccountProvider";
import { useCreateSignupCheckoutMutation } from "./register.service";
import {
  ADDITIONAL_LOCATION_PRICE,
  BASE_SUBSCRIPTION_PRICE,
  PRINTER_PRICE,
  formatGbp,
} from "../../constant/pricing";

function getStoredVerifyEmail() {
  try {
    return sessionStorage.getItem("sfbb_verify_email") || "";
  } catch (_) {
    return "";
  }
}

export default function MakePaymentPage() {
  const navigate = useNavigate();
  const { verifyEmail } = useContext(CreateAccountContext);
  const email = (verifyEmail || getStoredVerifyEmail() || "").trim().toLowerCase();

  const [createSignupCheckout, { isLoading }] =
    useCreateSignupCheckoutMutation();
  const [checkedExisting, setCheckedExisting] = useState(false);

  useEffect(() => {
    if (!email || checkedExisting) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await createSignupCheckout({ email }).unwrap();
        if (cancelled) return;
        if (result?.data?.alreadyPaid && result.data.sessionId) {
          toast.info(
            "Payment already completed — finish setting up your business."
          );
          navigate(`/create-restaurant?sessionId=${result.data.sessionId}`);
          return;
        }
      } catch (_) {
        /* allow manual subscribe */
      } finally {
        if (!cancelled) setCheckedExisting(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, checkedExisting, createSignupCheckout, navigate]);

  const startCheckout = async () => {
    if (!email || isLoading) return;
    try {
      const result = await createSignupCheckout({ email }).unwrap();

      if (result?.data?.alreadyPaid && result.data.sessionId) {
        toast.info(
          "Payment already completed — finish setting up your business."
        );
        navigate(`/create-restaurant?sessionId=${result.data.sessionId}`);
        return;
      }

      if (result?.data?.url) {
        window.location.href = result.data.url;
        return;
      }

      toast.error("Unable to start checkout. Please try again.");
    } catch (err) {
      toast.error(
        err?.data?.message || "Unable to start checkout. Please try again."
      );
    }
  };

  const canPay = Boolean(email);

  return (
    <div className="register-signincontainer signup-page auth-page payment-page">
      <div className="saas-card auth-card payment-card">
        <div className="auth-brand">
          <div className="auth-logo-wrap">
            <img src="/mark1.jpg" alt="SFBB Pro" />
          </div>
        </div>

        <div className="auth-heading">Complete your subscription</div>
        <p className="auth-copy">
          14-day free trial, then{" "}
          <strong className="payment-price-inline">
            {formatGbp(BASE_SUBSCRIPTION_PRICE)}/mo
          </strong>
          {" · "}1 location included
        </p>

        {email ? (
          <p className="payment-email-line">
            Paying as <strong>{email}</strong>
          </p>
        ) : (
          <p className="payment-email-line" style={{ color: "#f9d77e" }}>
            Email missing — go back and restart signup so payment can be linked
            to your account.
          </p>
        )}

        <div className="payment-note" role="note">
          <strong>After registration</strong> you can add extra locations (+
          {formatGbp(ADDITIONAL_LOCATION_PRICE)}/mo each) and order a label
          printer ({formatGbp(PRINTER_PRICE)}) from My Account.
        </div>

        <div className="stripe-buy-button">
          {canPay ? (
            <button
              type="button"
              className="signup-cta-btn payment-subscribe-btn"
              onClick={startCheckout}
              disabled={isLoading}
              style={isLoading ? { opacity: 0.7, cursor: "wait" } : undefined}
            >
              {isLoading
                ? "Starting checkout…"
                : `Subscribe · ${formatGbp(BASE_SUBSCRIPTION_PRICE)}/mo`}
            </button>
          ) : (
            <button
              type="button"
              className="signup-cta-btn payment-subscribe-btn"
              disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }}
            >
              Subscribe · {formatGbp(BASE_SUBSCRIPTION_PRICE)}/mo
            </button>
          )}
        </div>

        <div className="payment-trust">
          Secure payment · Cancel anytime
        </div>
      </div>

      <style>{`
        .payment-page.register-signincontainer {
          background-color: rgb(0, 111, 81);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 1.25rem 1rem;
          box-sizing: border-box;
        }

        .payment-page .saas-card.payment-card {
          max-width: 480px;
          width: 100%;
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(4px);
          border-radius: 28px;
          padding: 1.5rem 1.5rem 1.35rem;
          box-shadow: 0 25px 50px -10px rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
        }

        .payment-page .auth-brand {
          margin-bottom: 0.85rem;
        }

        .payment-page .auth-logo-wrap img {
          max-width: 200px;
        }

        .payment-page .auth-heading {
          margin-bottom: 0.45rem;
        }

        .payment-page .auth-copy {
          margin: 0 0 0.55rem;
          color: #e8f6f0;
          font-size: 0.95rem;
          line-height: 1.45;
        }

        .payment-price-inline {
          color: #f9d77e;
          font-weight: 700;
        }

        .payment-email-line {
          margin: 0 0 0.9rem;
          color: #d8efe6;
          font-size: 0.88rem;
        }

        .payment-email-line strong {
          color: #f9d77e;
          word-break: break-word;
        }

        .payment-note {
          text-align: center;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 14px;
          padding: 0.7rem 0.9rem;
          margin-bottom: 1.1rem;
          color: #e8f6f0;
          font-size: 0.84rem;
          line-height: 1.45;
        }

        .payment-note strong {
          color: #fff;
        }

        .payment-page .stripe-buy-button {
          margin: 0 0 0.85rem;
        }

        .payment-subscribe-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 48px;
          padding: 0 1.25rem;
          border-radius: 999px;
          background: #f9d77e;
          color: #1e3a2a !important;
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          box-sizing: border-box;
        }

        .payment-subscribe-btn:hover:not(:disabled) {
          background: #f5cc62;
          color: #1e3a2a !important;
        }

        .payment-trust {
          background: rgba(255, 255, 255, 0.05);
          text-align: center;
          padding: 10px 8px;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          color: #d8efe6;
          font-size: 0.8rem;
        }

        @media (max-width: 520px) {
          .payment-page .saas-card.payment-card {
            padding: 1.2rem 1rem 1.15rem;
          }
        }
      `}</style>
    </div>
  );
}
